import { useCallback, useRef } from 'react'
import type { MutableRefObject } from 'react'
import { useEvent } from './useStable'

/**
 * This is similar to React.useReducer, except it doesn't trigger a re-render
 * with every state update. This is good for rendering performance when you're
 * using non-rendering tools like react-spring's `animated` component.
 *
 * @param reducer the reducer function to use
 * @param startState the initial state
 * @param onUpdate what you'd like to happen when the state changes
 */
const useNonRenderingReducer = <S, A>(
  reducer: (state: S, action: A) => S,
  startState: S,
  onUpdate?: (state: S) => void
): [MutableRefObject<S>, (action: A) => void] => {
  const stateRef = useRef(startState)
  const handleUpdate = useEvent(onUpdate)
  const dispatch = useCallback(
    (action: A): void => {
      const prevState = stateRef.current
      const newState = reducer(prevState, action)
      if (newState !== stateRef.current) handleUpdate(newState)
      stateRef.current = newState
    },
    [handleUpdate, reducer]
  )
  return [stateRef, dispatch]
}

export default useNonRenderingReducer
